import { Component, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import moment from 'moment';
import { Subscription } from 'rxjs';
import { EnumLogTableauDeBordType, LogTableauDeBordService } from 'src/app/services/log-tableau-de-bord/log-tableau-de-bord.service';
import { HeaderCommunicationService } from 'src/app/services/node/header-communication.service';
import { Header } from 'src/app/utils/interfaces/new/header.interface';

import { debounceTime } from 'rxjs/operators';
import { ModalService } from 'src/app/services/modal/modal.service';
import { PatientService, FiltreStatsIdentites} from 'src/app/services/patient/patient.service';
import { ReferentielService } from 'src/app/services/referentiel/referentiel.service';

export interface JDVValue {
  id: string;
  libelle: string;
}


@Component({
  selector: 'app-tableau-de-bord',
  templateUrl: './tableau-de-bord.component.html',
  styleUrls: ['./tableau-de-bord.component.scss']
})
export class TableauDeBordComponent implements OnInit, OnDestroy {

  public pageTitle = 'Tableau de bord';

  public logsForm: FormGroup;

  public totalLogsDMPNonQualifie = 0;
  public totalLogsDMPQualifie = 0;
  public totalLogsMSSPatientQualifie = 0;
  public totalLogsMSSPatientNonQualifie = 0;
  public totalLogsMSSProQualifie = 0;
  public totalLogsMSSProNonQualifie = 0;
  public totalLogsDMPNonQualifieEchec = 0;
  public totalLogsDMPQualifieEchec = 0;
  public totalLogsMSSPatientQualifieEchec = 0;
  public totalLogsMSSPatientNonQualifieEchec = 0;
  public totalLogsMSSProQualifieEchec = 0;
  public totalLogsMSSProNonQualifieEchec = 0;

  public tauxLogsDmpNonQualifie = 0;
  public tauxLogsDmpQualifie = 0;
  public tauxLogsMssPatientQualifie = 0;
  public tauxLogsMssPatientNonQualifie = 0;
  public tauxLogsMssProQualifie = 0;
  public tauxLogsMssProNonQualifie = 0;
  public tauxLogsDmpNonQualifieEchec = 0;
  public tauxLogsDmpQualifieEchec = 0;
  public tauxLogsMssPatientQualifieEchec = 0;
  public tauxLogsMssPatientNonQualifieEchec = 0;
  public tauxLogsMssProQualifieEchec = 0;
  public tauxLogsMssProNonQualifieEchec = 0;

  public totalLogsDMPLecture = 0;
  public totalLogsDMPLectureEchec = 0;
  public tauxLogsDMPLecture = 0;
  public tauxLogsDMPLectureEchec = 0;

  public totalConsultations = 0;
  public totalPatients = 0; // File active distincte (utilisé pour les taux d'identités uniquement)
  public totalPatientsProvisoire = 0;
  public totalPatientsRecuperees = 0;
  public totalPatientValidees = 0;
  public totalPatientsQualifiees = 0;
  public totalPatientsQualifieesTransitoires = 0;

  public tauxProvisoire = 0;
  public tauxRecuperees = 0;
  public tauxValidees = 0;
  public tauxQualifiees = 0;
  public tauxQualifieesTransitoires = 0;
  public totalPatientsFictifsOuDouteux = 0;

  public indicateurProtocoles = 0;
  public indicateurEnfants: number | null = null;
  public indicateurMedecinTraitant: number | null = null;
  public indicateurRcp = 0;
  public ratioRcp = 0;
  public isSpecialiste = false;

  private _logsFormChange: Subscription;

  constructor(
    private _send: HeaderCommunicationService,
    private _formBuilder: FormBuilder,
    private _logSrv: LogTableauDeBordService,
    private _modal: ModalService,
    private _view: ViewContainerRef,
    private _patientService: PatientService,
    private _refSrv: ReferentielService
  ) {
    const head: Header = {
      backPage: 'utilitaires',
      isBack: true,
      title: this.pageTitle,
    };
    this._send.announceHeader(head);
  }

  public typeDocuments: Array<JDVValue> = [];

  ngOnInit(): void {
    this._loadReferentiels();

    this.logsForm = this._formBuilder.group({
      finess: [''],
      rpps: [''],
      finessDestinataire: [''],
      rppsDestinataire: [''],
      dateDebut: [''],
      dateFin: [''],
      typeDocument: [''],
    });

    this._logsFormChange = this.logsForm.valueChanges.pipe(debounceTime(1000)).subscribe(d => {
      this._changeLogs();
    });
    this._changeLogs();
  }

  private async _loadReferentiels(): Promise<void> {
    try {
      const temp = await this._refSrv.findRefListTableHypermed('RefTypeCodeDMP');
      for (const value of temp) {
        this.typeDocuments.push({
          id: value.id.indexOf('++') > -1 ? value.id.split('++')[0] : value.id,
          libelle: value.libelle,
        });
      }
    } catch (e) {
      console.error('Erreur lors du chargement de RefTypeCodeDMP', e);
    }
  }

  ngOnDestroy(): void {
    if (this._logsFormChange) {
      this._logsFormChange.unsubscribe();
    }
  }

  private async getStatsIdentites(): Promise<void> {
    try {
      const filtre: FiltreStatsIdentites = {
        finess: this.logsForm.get('finess').value || undefined,
        rpps: this.logsForm.get('rpps').value || undefined,
        dateDebut: this.logsForm.get('dateDebut').value || undefined,
        dateFin: this.logsForm.get('dateFin').value || undefined,
      };
  
      const [stats, coordonneStats] = await Promise.all([
        this._patientService.findPatientIdentityStats(filtre),
        this._patientService.findStructuresExerciceCoordonneStats(filtre)
      ]);

      this.totalConsultations = stats?.totalConsultations ?? 0;
      this.totalPatients = stats?.total ?? 0;
      this.totalPatientsProvisoire = stats?.provisoire ?? 0;
      this.totalPatientsRecuperees = stats?.recuperees ?? 0;
      this.totalPatientValidees = stats?.validees ?? 0;
      this.totalPatientsQualifiees = stats?.qualifiees ?? 0;
      this.totalPatientsQualifieesTransitoires = stats?.qualifieesTransitoires ?? 0;
      this.tauxProvisoire = stats? Math.round(stats.tauxProvisoire*10)/10 : 0;
      this.tauxRecuperees = stats? Math.round(stats.tauxRecuperees*10)/10 : 0;
      this.tauxValidees = stats? Math.round(stats.tauxValidees*10)/10 : 0;
      this.tauxQualifiees = stats? Math.round(stats.tauxQualifiees*10)/10 : 0;
      this.tauxQualifieesTransitoires = stats? Math.round(stats.tauxQualifieesTransitoires*10)/10 : 0;
      this.totalPatientsFictifsOuDouteux = stats?.fictifsOuDouteux ?? 0;
      this.indicateurProtocoles = coordonneStats?.indicateurProtocoles ?? 0;
      this.indicateurEnfants = coordonneStats?.indicateurEnfants ?? null;
      this.indicateurMedecinTraitant = coordonneStats?.indicateurMedecinTraitant ?? null;
      this.indicateurRcp = coordonneStats?.indicateurRcp ?? 0;
      this.ratioRcp = coordonneStats?.ratioRcp ?? 0;
      this.isSpecialiste = coordonneStats?.isSpecialiste ?? false;

    } catch (e) {
      // Si la requête échoue (ex: 404 praticien introuvable), on remet les compteurs à zéro
      this.totalConsultations = 0;
      this.totalPatients = 0;
      this.totalPatientsProvisoire = 0;
      this.totalPatientsRecuperees = 0;
      this.totalPatientValidees = 0;
      this.totalPatientsQualifiees = 0;
      this.totalPatientsQualifieesTransitoires = 0;
      this.tauxProvisoire = 0;
      this.tauxRecuperees = 0;
      this.tauxValidees = 0;
      this.tauxQualifiees = 0;
      this.tauxQualifieesTransitoires = 0;
      this.totalPatientsFictifsOuDouteux = 0;

      this.indicateurProtocoles = 0;
      this.indicateurEnfants = 0;
      this.indicateurMedecinTraitant = 0;
      this.indicateurRcp = 0;
      this.ratioRcp = 0;
      this.isSpecialiste = false;
    }
  }

  private async _changeLogs(): Promise<void> {
    await Promise.all([
      this._loadLogs(),
      this.getStatsIdentites(),
    ]);
    this.calculateRates();
  }

  private async _loadLogs(): Promise<void> {
    const finessExpediteur = this.logsForm.get('finess')?.value || '';
    const rppsExpediteur = this.logsForm.get('rpps')?.value || '';
    const finessDestinataire = this.logsForm.get('finessDestinataire')?.value || '';
    const rppsDestinataire = this.logsForm.get('rppsDestinataire')?.value || '';
    const dateDebut = this.logsForm.get('dateDebut')?.value ? encodeURIComponent(this.logsForm.get('dateDebut')?.value) : '';
    const dateFin = this.logsForm.get('dateFin')?.value ? encodeURIComponent(this.logsForm.get('dateFin')?.value) : '';
    const typeDocument = this.logsForm.get('typeDocument')?.value || '';

    const [
      logsDMPNonQualifie,
      logsDMPQualifie,
      logsMSSPatientNonQualifie,
      logsMSSPatientQualifie,
      logsMSSProNonQualifie,
      logsMSSProQualifie,
      logsDMPNonQualifieEchec,
      logsDMPQualifieEchec,
      logsDMPLectureEchec,
      logsMSSPatientNonQualifieEchec,
      logsMSSPatientQualifieEchec,
      logsMSSProNonQualifieEchec,
      logsMSSProQualifieEchec,
      logsDMPLecture
    ] = await Promise.all([
      this._logSrv.getLogs({ type: EnumLogTableauDeBordType.DMP_INS_NON_QUALIFIE, finessExpediteur, rppsExpediteur, dateDebut, dateFin, typeDocument, isSuccessful: true }),
      this._logSrv.getLogs({ type: EnumLogTableauDeBordType.DMP_INS_QUALIFIE, finessExpediteur, rppsExpediteur, dateDebut, dateFin, typeDocument, isSuccessful: true }),
      this._logSrv.getLogs({ type: EnumLogTableauDeBordType.MSS_PATIENT_INS_NON_QUALIFIE, finessExpediteur, rppsExpediteur, dateDebut, dateFin, typeDocument, isSuccessful: true }),
      this._logSrv.getLogs({ type: EnumLogTableauDeBordType.MSS_PATIENT_INS_QUALIFIE, finessExpediteur, rppsExpediteur, dateDebut, dateFin, typeDocument, isSuccessful: true }),
      this._logSrv.getLogs({ type: EnumLogTableauDeBordType.MSS_PRO_INS_NON_QUALIFIE, finessExpediteur, rppsExpediteur, finess: finessDestinataire, rpps: rppsDestinataire, dateDebut, dateFin, typeDocument, isSuccessful: true }),
      this._logSrv.getLogs({ type: EnumLogTableauDeBordType.MSS_PRO_INS_QUALIFIE, finessExpediteur, rppsExpediteur, finess: finessDestinataire, rpps: rppsDestinataire, dateDebut, dateFin, typeDocument, isSuccessful: true }),
      this._logSrv.getLogs({ type: EnumLogTableauDeBordType.DMP_INS_NON_QUALIFIE, finessExpediteur, rppsExpediteur, dateDebut, dateFin, typeDocument, isSuccessful: false }),
      this._logSrv.getLogs({ type: EnumLogTableauDeBordType.DMP_INS_QUALIFIE, finessExpediteur, rppsExpediteur, dateDebut, dateFin, typeDocument, isSuccessful: false }),
      this._logSrv.getLogs({ type: EnumLogTableauDeBordType.DMP_LECTURE, finessExpediteur, rppsExpediteur, dateDebut, dateFin, typeDocument, isSuccessful: false }),
      this._logSrv.getLogs({ type: EnumLogTableauDeBordType.MSS_PATIENT_INS_NON_QUALIFIE, finessExpediteur, rppsExpediteur, dateDebut, dateFin, typeDocument, isSuccessful: false }),
      this._logSrv.getLogs({ type: EnumLogTableauDeBordType.MSS_PATIENT_INS_QUALIFIE, finessExpediteur, rppsExpediteur, dateDebut, dateFin, typeDocument, isSuccessful: false }),
      this._logSrv.getLogs({ type: EnumLogTableauDeBordType.MSS_PRO_INS_NON_QUALIFIE, finessExpediteur, rppsExpediteur, finess: finessDestinataire, rpps: rppsDestinataire, dateDebut, dateFin, typeDocument, isSuccessful: false }),
      this._logSrv.getLogs({ type: EnumLogTableauDeBordType.MSS_PRO_INS_QUALIFIE, finessExpediteur, rppsExpediteur, finess: finessDestinataire, rpps: rppsDestinataire, dateDebut, dateFin, typeDocument, isSuccessful: false }),
      this._logSrv.getLogs({ type: EnumLogTableauDeBordType.DMP_LECTURE, finessExpediteur, rppsExpediteur, dateDebut, dateFin, typeDocument, isSuccessful: true })
    ]);

    this.totalLogsDMPNonQualifie = logsDMPNonQualifie.total;
    this.totalLogsDMPQualifie = logsDMPQualifie.total;
    this.totalLogsMSSPatientNonQualifie = logsMSSPatientNonQualifie.total;
    this.totalLogsMSSPatientQualifie = logsMSSPatientQualifie.total;
    this.totalLogsMSSProNonQualifie = logsMSSProNonQualifie.total;
    this.totalLogsMSSProQualifie = logsMSSProQualifie.total;
    this.totalLogsDMPNonQualifieEchec = logsDMPNonQualifieEchec.total;
    this.totalLogsDMPQualifieEchec = logsDMPQualifieEchec.total;
    this.totalLogsDMPLectureEchec = logsDMPLectureEchec.total;
    this.totalLogsMSSPatientNonQualifieEchec = logsMSSPatientNonQualifieEchec.total;
    this.totalLogsMSSPatientQualifieEchec = logsMSSPatientQualifieEchec.total;
    this.totalLogsMSSProNonQualifieEchec = logsMSSProNonQualifieEchec.total;
    this.totalLogsMSSProQualifieEchec = logsMSSProQualifieEchec.total;
    this.totalLogsDMPLecture = logsDMPLecture.total;
  }

  public calculateRates(): void {
    if (this.totalConsultations === 0) {
      this.tauxLogsDmpNonQualifie = 0;
      this.tauxLogsDmpQualifie = 0;
      this.tauxLogsMssPatientQualifie = 0;
      this.tauxLogsMssPatientNonQualifie = 0;
      this.tauxLogsMssProQualifie = 0;
      this.tauxLogsMssProNonQualifie = 0;
      this.tauxLogsDmpNonQualifieEchec = 0;
      this.tauxLogsDmpQualifieEchec = 0;
      this.tauxLogsMssPatientQualifieEchec = 0;
      this.tauxLogsMssPatientNonQualifieEchec = 0;
      this.tauxLogsMssProQualifieEchec = 0;
      this.tauxLogsMssProNonQualifieEchec = 0;
      this.tauxLogsDMPLecture = 0;
      this.tauxLogsDMPLectureEchec = 0;
      return;
    }

    // Math.round(valeur * 10) / 10 permet d'arrondir à 1 décimale
    this.tauxLogsDmpNonQualifie = Math.round((this.totalLogsDMPNonQualifie / this.totalConsultations * 100) * 10) / 10;
    this.tauxLogsDmpQualifie = Math.round((this.totalLogsDMPQualifie / this.totalConsultations * 100) * 10) / 10;
    this.tauxLogsMssPatientQualifie = Math.round((this.totalLogsMSSPatientQualifie / this.totalConsultations * 100) * 10) / 10;
    this.tauxLogsMssPatientNonQualifie = Math.round((this.totalLogsMSSPatientNonQualifie / this.totalConsultations * 100) * 10) / 10;
    this.tauxLogsMssProQualifie = Math.round((this.totalLogsMSSProQualifie / this.totalConsultations * 100) * 10) / 10;
    this.tauxLogsMssProNonQualifie = Math.round((this.totalLogsMSSProNonQualifie / this.totalConsultations * 100) * 10) / 10;
    this.tauxLogsDmpNonQualifieEchec = Math.round((this.totalLogsDMPNonQualifieEchec / this.totalConsultations * 100) * 10) / 10;
    this.tauxLogsDmpQualifieEchec = Math.round((this.totalLogsDMPQualifieEchec / this.totalConsultations * 100) * 10) / 10;
    this.tauxLogsMssPatientQualifieEchec = Math.round((this.totalLogsMSSPatientQualifieEchec / this.totalConsultations * 100) * 10) / 10;
    this.tauxLogsMssPatientNonQualifieEchec = Math.round((this.totalLogsMSSPatientNonQualifieEchec / this.totalConsultations * 100) * 10) / 10;
    this.tauxLogsMssProQualifieEchec = Math.round((this.totalLogsMSSProQualifieEchec / this.totalConsultations * 100) * 10) / 10;
    this.tauxLogsMssProNonQualifieEchec = Math.round((this.totalLogsMSSProNonQualifieEchec / this.totalConsultations * 100) * 10) / 10;
    this.tauxLogsDMPLecture = Math.round((this.totalLogsDMPLecture / this.totalConsultations * 100) * 10) / 10;
    this.tauxLogsDMPLectureEchec = Math.round((this.totalLogsDMPLectureEchec / this.totalConsultations * 100) * 10) / 10;
  }

  public async export() {
    const loader = this._modal.openLoading(this._view);
    try {
      const res = await this._logSrv.exportLogs();
      this.saveAsFile(res, 'export_logs_tableau_de_bord.csv', 'text/csv');
    } catch (e) {
      this._modal.openAlert(
      this._view,
      'Erreur',
      "Une erreur est survenue lors du téléchargement de l'export."
    );
      console.log(e);
    } finally {
      this._modal.close(loader);
    }
  }

  public async exportStatsExerciceCoordonne() {
    const loader = this._modal.openLoading(this._view);
    try {
      const filtre: FiltreStatsIdentites = {
        finess: this.logsForm.get('finess').value || undefined,
        rpps: this.logsForm.get('rpps').value || undefined,
        dateDebut: this.logsForm.get('dateDebut').value || undefined,
        dateFin: this.logsForm.get('dateFin').value || undefined,
      };
      const blob = await this._patientService.exportStructuresExerciceCoordonneStats(filtre);
      this.saveAsFile(blob, 'export_structures_exercice_coordonne.csv', 'text/csv');
    } catch (e) {
      this._modal.openAlert(
        this._view,
        'Erreur',
        "Une erreur est survenue lors du téléchargement de l'export CSV."
      );
      console.error(e);
    } finally {
      this._modal.close(loader);
    }
  }

  private saveAsFile(buffer: any, fileName: string, fileType: string): void {
    const data: Blob = new Blob([buffer], { type: fileType });
    var downloadURL = window.URL.createObjectURL(data);
    var link = document.createElement('a');
    link.href = downloadURL;
    link.download = fileName;
    link.click();
  }

}
